import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import SimpleBackdrop from "components/MDLoader_Backdrop";
import crLogo from "../../assets/images/icons/catering-rewards/cr-logo-black.png";
import { useOtp } from "context/OtpContext";
import PhoneIcon from "@mui/icons-material/Phone";
import { OTP_LENGTH } from "constants/authConstants";
import IdentifierInput from "layouts/restaurant/components/OtpSignInModal/IdentifierInput";
import OtpInputForm from "layouts/restaurant/components/OtpSignInModal/OtpInputForm";
import KnowMeForm from "layouts/restaurant/components/AuthModal/KnowMeForm";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

const isPhoneNumber = (value) => {
  const digitsOnly = value.replace(/\D/g, "");
  return /^(\+?[0-9]{10,15})$/.test(digitsOnly);
};

const SignupModal = ({
  isOpen,
  onClose,
  gtmCode,
  campaignCode,
  referrerCode,
  campaignDetails,
  landingData,
}) => {
  const { isOtpSent, isLoading, message, identifier } = useOtp();
  const [level, setLevel] = useState(0);

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ p: 0, textAlign: "center" }}>
        <Box mt={2}>
          <img src={crLogo} alt="Logo" className="mx-auto" style={{ width: 150, height: "auto" }} />
        </Box>

        <MDTypography
          variant="h2"
          sx={{
            fontSize: { xs: "1.25rem", md: "1.5rem" },
            fontWeight: 700,
            color: "#1A202C",
            mt: 1.5,
            mb: 1,
            textTransform: "none",
            letterSpacing: "-0.02em",
          }}
        >
          Welcome to Catering Rewards!
        </MDTypography>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <>
          {level === 0 ? (
            <>
              {isOtpSent && (
                <MDBox mx={2} p={2} textAlign="center" minWidth={{ xs: "320px", xl: "380px" }}>
                  {/*Show Email/Phone Icon On OTP Sent */}

                  <MDBox
                    mb={"24px"}
                    mt={"12px"}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Avatar
                      sx={{
                        bgcolor: "#CEFFE1",
                        width: 48,
                        height: 48,
                      }}
                    >
                      {isPhoneNumber(identifier) ? (
                        <PhoneIcon sx={{ fontSize: 24, color: "rgb(16, 121, 72)" }} />
                      ) : (
                        <MailOutlineIcon sx={{ fontSize: 24, color: "rgb(16, 121, 72)" }} />
                      )}
                    </Avatar>
                  </MDBox>

                  <MDTypography
                    sx={{ color: "#101828", fontSize: "28px" }}
                    variant="h4"
                    fontWeight="medium"
                    mt={1}
                    mb={"20px"}
                  >
                    {`Please check your ${isPhoneNumber(identifier) ? "SMS" : "email"} `}
                  </MDTypography>
                </MDBox>
              )}

              <MDBox px={"24px"}>
                {!isOtpSent ? (
                  <IdentifierInput
                    label="Enter your personal email or mobile number"
                    isSignup={true}
                    gtmCode={gtmCode}
                    campaignCode={campaignCode}
                    campaignDetails={campaignDetails}
                    storeId={landingData?.storeId}
                    brandId={landingData?.brandId}
                  />
                ) : (
                  <OtpInputForm
                    gtmCode={gtmCode}
                    setLevel={setLevel}
                    length={OTP_LENGTH}
                    campaignCode={campaignCode}
                    campaignDetails={campaignDetails}
                    storeId={landingData?.storeId}
                    brandId={landingData?.brandId}
                  />
                )}
              </MDBox>
            </>
          ) : (
            <>
              <MDBox mx={2} p={2} textAlign="center" minWidth={{ xs: "320px", xl: "380px" }}>
                <MDTypography
                  sx={{ color: "#101828", fontSize: "28px" }}
                  variant="h4"
                  fontWeight="medium"
                  mt={1}
                  mb={"20px"}
                >
                  Let us get to know you
                </MDTypography>
              </MDBox>

              <MDBox pb={"24px"} px={"24px"}>
                <KnowMeForm
                  setLevel={setLevel}
                  storeId={landingData?.storeId}
                  brandId={landingData?.brandId}
                  campaignCode={campaignCode}
                  referrerCode={referrerCode}
                  gtmCode={gtmCode}
                  campaignDetails={campaignDetails}
                />
              </MDBox>

              <Typography
                mx={2}
                px={2}
                pb={2}
                variant="body2"
                textAlign={"center"}
                style={{
                  maxWidth: "360px",
                  fontSize: "0.8rem",
                  color: message && message.type === "error" ? "#f44336" : "#238856",
                  fontWeight: 500,
                }}
              >
                {message?.type === "error" && message?.message}
              </Typography>
            </>
          )}
          {isLoading && <SimpleBackdrop />}
        </>
      </DialogContent>
    </Dialog>
  );
};

SignupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  gtmCode: PropTypes.string,
  campaignCode: PropTypes.string,
  referrerCode: PropTypes.string,
  campaignDetails: PropTypes.object,
  landingData: PropTypes.object,
};

export default SignupModal;
